import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useRef } from "react";
import { Field, Form } from "@boundary/components/form";
import {
  OtpInput,
  OtpGroup,
  OtpSlot
} from "@boundary/components/inputOtp";
import { Button } from "@boundary/components/button";
import { OnboardingElement } from "@boundary/widgets/onboarding";
import { LoginFlowData, LoginFlowSteps } from "@boundary/pages/3.login/loginFlow";
import { Page } from "@boundary/layouts/pageLayout";
import { useAuth } from "@boundary/_hooks";
import { OtpRetryButton } from "@boundary/components/otpRetryButton";
import styles from "./otp.module.scss";

export const OtpStep: OnboardingElement<LoginFlowData, LoginFlowSteps> = (props) => {
  const { login } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const form = useForm<LoginFlowData>({
    resolver: zodResolver(
      LoginFlowData.pick({
        otp: true
      })
    ),
    defaultValues: props.data
  });

  const handleSubmit = form.handleSubmit(async (values: Pick<LoginFlowData, "otp">) => {
    // OTP уже очищен через валидацию Zod (transform)
    console.log(`[OtpStep] Submitting OTP: phone='${props.data.phone}', otp='${values.otp}', otp_length=${values.otp.length}`);
    const result = await login(props.data.phone, values.otp);
    console.log(`[OtpStep] Login result:`, result);

    if (result) {
      return props.onComplete("name", { ...props.data, ...values });
    }
  });

  // Отслеживаем значение OTP для нормализации и автоматической отправки
  const otpValue = form.watch("otp");
  const autoSubmitRef = useRef(false);
  
  useEffect(() => {
    if (!otpValue || autoSubmitRef.current) return;
    
    // Нормализуем значение: удаляем все нецифровые символы
    const normalizedValue = otpValue.replace(/\D/g, "");
    
    // Если значение изменилось после нормализации, обновляем его
    if (normalizedValue !== otpValue) {
      console.log(`[OtpStep] Normalizing OTP value: '${otpValue}' -> '${normalizedValue}'`);
      form.setValue("otp", normalizedValue, { shouldValidate: true, shouldTouch: true });
      return; // Не отправляем, если значение было нормализовано
    }
    
    // Если код полный (6 цифр), автоматически отправляем форму
    if (normalizedValue.length === 6) {
      console.log(`[OtpStep] Auto-submitting OTP: '${normalizedValue}'`);
      autoSubmitRef.current = true; // Предотвращаем повторную отправку
      setTimeout(() => {
        form.handleSubmit(handleSubmit)();
        // Сбрасываем флаг через небольшую задержку
        setTimeout(() => {
          autoSubmitRef.current = false;
        }, 1000);
      }, 300);
    }
  }, [otpValue, form, handleSubmit]);

  // Обработка вставки кода на уровне контейнера (для автозаполнения из SMS)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const pastedText = e.clipboardData?.getData("text") || "";
      // Извлекаем только цифры из вставленного текста
      const digitsOnly = pastedText.replace(/\D/g, "").slice(0, 6);
      console.log(`[OtpStep] Paste detected on container: raw='${pastedText}', digits='${digitsOnly}'`);
      
      if (digitsOnly.length > 0) {
        // Устанавливаем значение через react-hook-form
        // useEffect выше обработает нормализацию и автопередачу
        form.setValue("otp", digitsOnly, { shouldValidate: true, shouldTouch: true });
      }
    };

    container.addEventListener("paste", handlePaste);
    return () => {
      container.removeEventListener("paste", handlePaste);
    };
  }, [form]);

  return (
    <Page screenBackground="domain">
      <Page.SafeArea />
      <Page.DisplayZone>
        <Form {...form}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <Page.DisplayZone.Body className={styles.body}>
              <h1>Спасибо! Отправили<br />код на ваш номер 👌</h1>
              <p>Код отправлен на {props.data.phone}</p>
              <div ref={containerRef}>
                <Field
                  control={form.control}
                  name="otp"
                  disabled={form.formState.isSubmitting}
                  component={(fieldProps) => (
                    <OtpInput
                      {...fieldProps}
                      maxLength={6}
                      onComplete={handleSubmit}
                      autoFocus
                      value={fieldProps.value || ""}
                      pattern="[0-9]*"
                      onChange={(value: string) => {
                        // Нормализуем значение: удаляем все нецифровые символы
                        const normalizedValue = value.replace(/\D/g, "");
                        console.log(`[OtpStep] Input changed: raw='${value}', normalized='${normalizedValue}'`);
                        fieldProps.onChange(normalizedValue);
                      }}
                    >
                      <OtpGroup className={styles.otpGroup}>
                        <OtpSlot index={0} />
                        <OtpSlot index={1} />
                        <OtpSlot index={2} />
                        <OtpSlot index={3} />
                        <OtpSlot index={4} />
                        <OtpSlot index={5} />
                      </OtpGroup>
                    </OtpInput>
                  )}
                />
              </div>
              <OtpRetryButton phone={props.data.phone} />
            </Page.DisplayZone.Body>
            <Page.DisplayZone.Sticky className={styles.buttonPad}>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Подтвердить
              </Button>
            </Page.DisplayZone.Sticky>
          </form>
        </Form>
      </Page.DisplayZone>
    </Page>
  );
};
